export const POSTS = [
    {
        slug: "indica-vs-sativa-vs-hybrid",
        title: "Indica vs. Sativa vs. Hybrid: What Actually Sets Them Apart",
        excerpt:
            "The three classic cannabis families, what they're generally known for, and how to pick the right lane for your evening — no botany degree required.",
        date: "October 20, 2026",
        readTime: "5 min read",
        tag: "Education",
        image:
            "https://images.unsplash.com/photo-1457573294499-86ac96b336ba?crop=entropy&cs=srgb&fm=jpg&q=85",
        html: `
<p>Walk into any dispensary menu — including <a href="/shop">ours</a> — and you'll see products grouped into three classic families: indica, sativa and hybrid. Here's the honest, no-hype version of what those words mean and how to use them.</p>
<h2>Indica: the couch-callers</h2>
<p>Indica strains are generally associated with deep body relaxation. Many people reach for them in the evening, for winding down after work or pairing with a movie. On our menu, indica favorites sit under <a href="/shop?category=indica">Indica</a> — look for notes like "relaxing" or "nighttime" in the description.</p>
<h2>Sativa: the daytime crowd</h2>
<p>Sativa strains are typically described as uplifting, cerebral and social. Think music, creating, cleaning the apartment with the playlist on. Browse the <a href="/shop?category=sativa">Sativa</a> shelf if you want something for daylight hours.</p>
<h2>Hybrid: both worlds, balanced</h2>
<p>Hybrids cross indica and sativa genetics, which is why they're the biggest section on most menus. Some lean relaxing ("indica-dominant"), some lean bright ("sativa-dominant"). The <a href="/shop?category=hybrid">Hybrid</a> collection is the right starting point if you're not sure what you like yet.</p>
<h2>What actually determines your experience</h2>
<ul>
<li><strong>Cannabinoids</strong> — THC and CBD content matter more than the label on the bag.</li>
<li><strong>Terpenes</strong> — the aromatic compounds (myrcene, limonene, caryophyllene) that give each strain its smell and character.</li>
<li><strong>Your own tolerance</strong> — start low, especially with anything new.</li>
</ul>
<h2>Where exotics fit</h2>
<p>"Exotic" isn't a third family — it's a shorthand for rare, top-shelf genetics with standout bag appeal. We keep ours in <a href="/shop?category=exotic">Exotics</a>, and they move fast.</p>
<p>Still stuck? Call the shop at <a href="tel:+12027134325">(202) 713-4325</a> — we're open 24 hours, Monday through Sunday, and we'll steer you right.</p>
`,
    },
    {
        slug: "how-to-order-weed-delivery-washington-dc",
        title: "How to Order Delivery or Pickup from District Greenz DMV",
        excerpt:
            "The whole flow, start to finish: browse the live menu, build your bag, choose delivery or pickup, pay cash, show a valid 21+ ID. That's it.",
        date: "October 20, 2026",
        readTime: "3 min read",
        tag: "How To Order",
        image:
            "https://images.pexels.com/photos/12655421/pexels-photo-12655421.jpeg?auto=compress&cs=tinysrgb&q=85",
        html: `
<p>Ordering from District Greenz DMV takes about two minutes. Here's the full rundown.</p>
<h2>Step 1 — Browse the live menu</h2>
<p>Everything on <a href="/shop">the menu</a> is synced straight from the shop in real time, so what you see is what we actually have. Filter by <a href="/shop?category=hybrid">Hybrid</a>, <a href="/shop?category=edibles">Edibles</a>, <a href="/shop?category=cartridges">Cartridges</a>, <a href="/shop?category=concentrates">Concentrates</a> and more, or use the search bar.</p>
<h2>Step 2 — Build your bag</h2>
<p>Pick your size or option on variable products, add to your bag, and apply any coupon code you have at the cart. Prices are always verified against the live store when you check out.</p>
<h2>Step 3 — Choose delivery or pickup</h2>
<ul>
<li><strong>Local delivery</strong> — we bring your order to your door in Washington DC, including the 20007, 20011 and 20009 zip codes.</li>
<li><strong>Store pickup</strong> — order ahead and collect at the shop. No waiting around.</li>
</ul>
<h2>Step 4 — Pay cash, show ID</h2>
<p>Payment is cash on delivery or pay at pickup. Have a valid government-issued 21+ ID ready at hand-off — no ID, no product. It's the rule, and we follow it every time.</p>
<p>Prefer to talk it through? Call us any time at <a href="tel:+12027134325">(202) 713-4325</a> — we answer 24 hours a day, Monday through Sunday. Current deals live <a href="/deals">here</a>.</p>
`,
    },
    {
        slug: "beginners-guide-to-edibles",
        title: "A Beginner's Guide to Edibles: Start Low, Go Slow",
        excerpt:
            "Gummies, chocolates and everything in between — how edibles work, why timing catches people out, and how to pick a sensible first dose.",
        date: "October 20, 2026",
        readTime: "4 min read",
        tag: "Education",
        image:
            "https://images.pexels.com/photos/15360877/pexels-photo-15360877.jpeg?auto=compress&cs=tinysrgb&q=85",
        html: `
<p>Edibles are the most misunderstood section of any menu. Here's what first-timers should know before browsing our <a href="/shop?category=edibles">edibles shelf</a>.</p>
<h2>Why edibles feel different</h2>
<p>When you eat cannabis, your body processes THC through the liver, which converts it into a longer-lasting compound. The result is a slower onset and a much longer experience than smoking or vaping — which is exactly the point, as long as you plan for it.</p>
<h2>The timing trap</h2>
<p>The number one beginner mistake: taking more because "nothing's happening" after 20 minutes. Edibles can take <strong>30 minutes to 2 hours</strong> to fully kick in. Wait at least two hours before even considering another piece.</p>
<h2>Start low, go slow</h2>
<ul>
<li>If the package lists total milligrams, divide by the number of pieces to get the per-piece dose.</li>
<li>First time? One low-dose piece is plenty. You can always take more next time — the reverse isn't true.</li>
<li>Keep them locked away from kids and pets. They look like candy for a reason.</li>
</ul>
<h2>Picking your first edible</h2>
<p>Gummies are the classic starting point — consistent, easy to portion, easy to track. Our <a href="/shop?category=edibles">edibles menu</a> shows the full live selection with sizes and prices.</p>
<p>Questions about a specific product? Call the shop at <a href="tel:+12027134325">(202) 713-4325</a> — open 24 hours, Monday through Sunday.</p>
`,
    },
    {
        slug: "dc-gifting-model-explained",
        title: "DC's Gifting Model, Explained (and Why Art Is Involved)",
        excerpt:
            "Cannabis isn't sold off the shelf in DC — it's shared as a gift. Here's how the gifting model works and why every art purchase at our shop comes with a free gift.",
        date: "October 20, 2026",
        readTime: "4 min read",
        tag: "Washington DC",
        image:
            "https://images.pexels.com/photos/12672328/pexels-photo-12672328.jpeg?auto=compress&cs=tinysrgb&q=85",
        html: `
<p>If you're new to the District, the cannabis scene here works a little differently than in states with recreational dispensaries — and it's the reason our shop doubles as an art gallery.</p>
<h2>The short version</h2>
<p>Under DC's Initiative 71 framework, cannabis itself isn't retailed over the counter the way it is in a state-licensed dispensary. Instead, local businesses operate on a gifting model: customers purchase a legal item — art, prints, merch — and receive cannabis as a gift alongside it. No sales of cannabis itself, just a gift with a purchase.</p>
<h2>How it works at District Greenz</h2>
<p>Every art purchase at our shop comes with a free cannabis gift. You browse the <a href="/shop">menu</a> the same way you would anywhere else, your bag is priced clearly, and the gift is shared with your purchase when you collect it — delivered to your door or picked up at the shop.</p>
<h2>What never changes</h2>
<ul>
<li>You must be <strong>21 or older</strong> with a valid government-issued ID. Every order, no exceptions.</li>
<li>Cannabis can't be consumed in public spaces in DC. Enjoy it on private property.</li>
<li>Don't drive under the influence. Full stop.</li>
</ul>
<h2>Why the gallery part is real</h2>
<p>The art isn't a loophole gimmick — it's the heart of the shop. We feature DC artists and keep the space feeling more like a gallery that happens to be extremely generous with gifts. Come through 10 Florida Ave NW, or browse the <a href="/shop?category=merchandise">merch and art side</a> of the menu online.</p>
`,
    },
];

export const getPost = (slug) => POSTS.find((p) => p.slug === slug);
